/**
 * UploadSheetsModal — Iter 38.5 plan-admin upload + Iter 40 bulk-ingest gate.
 *
 * Workflow (2026-09-14 — resumable/chunked/integrity-checked upload):
 *   1. Admin picks a PDF (single-sheet OR multi-sheet plan package).
 *   2. Client hashes the file (SHA-256), opens/resumes an upload
 *      session, PUTs any missing chunks, then calls `complete` — which
 *      verifies the hash server-side and returns the batch. Reselecting
 *      the SAME file after a reload/dropped connection resumes instead
 *      of re-uploading from scratch (see `uploadResumable` below).
 *   3. Modal closes + redirects to /project/{id}/ingest/{batchId} for
 *      review + publish gate.
 *
 * Per Iter 40 directive: NO sheet enters the live plan set without admin
 * confirmation. The old "type sheet_number into a form and post directly
 * to /sheets/upload" flow is REPLACED by the bulk-ingest pipeline so OCR
 * misreads never pollute live plans.
 */
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, Upload, Loader2, AlertCircle, FileText, Layers, Link as LinkIcon } from "lucide-react";

const CHUNK_UPLOAD_CONCURRENCY = 3;
const CHUNK_PUT_MAX_ATTEMPTS = 3;
const CHUNK_PUT_RETRY_DELAYS_MS = [500, 1500, 3000];

async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 2026-09-29 (post-deployment repair, item 6.6) — the resume cache key
 * used to include a slice of the CURRENT bearer/access token. Access
 * tokens rotate on every silent refresh (see `lib/api.js::
 * refreshAccessToken`), which is routine on a long upload over a slow
 * mobile connection — a token refresh mid-session, or simply reopening
 * the tab later after the old token expired, produced a DIFFERENT key
 * than the one the in-progress session was cached under, so the
 * resumable-upload feature silently failed to find its own cache and
 * started a brand-new session instead of resuming. `userId` is the
 * account's stable database id (unaffected by token rotation), so the
 * key stays valid for the lifetime of the same signed-in user on this
 * browser, and two different accounts can never collide on it. */
function sessionStorageKey(userId, projectId, mode, filename, size) {
  return `fop_upload_session::${userId || "anon"}::${projectId}::${mode}::${filename}::${size}`;
}

/** Resumable/chunked/integrity-checked upload (2026-09-14 directive;
 * hardened 2026-09-29 — post-deployment repair item 6). Survives a
 * browser reload mid-upload: the session id + declared hash are cached
 * in localStorage keyed by (user, project, mode, filename, size), so
 * re-selecting the SAME file after a reload resumes from whichever
 * chunks the server already has instead of re-uploading from scratch.
 * `onStage` reports a truthful phase name so the UI never claims
 * "processing" before the server has actually verified the assembled
 * file. */
async function uploadResumable(userId, projectId, mode, file, onProgress, onStage) {
  onStage("hashing");
  const hash = await sha256Hex(file);
  const key = sessionStorageKey(userId, projectId, mode, file.name, file.size);
  let sessionId = null;
  let chunkSize = null;
  let totalChunks = null;
  let received = new Set();

  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed.hash === hash) {
        // 2026-09-29 (item 6.4/6.5) — validate against SERVER state, not
        // just cache presence, and don't silently swallow a transient
        // network failure as "no session to resume" — that would start
        // a duplicate session (and duplicate chunk uploads) for a
        // session that is actually still fine server-side. Retry a
        // couple of times on network error; only treat a genuine 404
        // (server confirms the session is gone) as "start fresh."
        let resumed = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const { data } = await api.get(`/upload_sessions/${parsed.sessionId}`);
            resumed = data;
            break;
          } catch (err) {
            if (err?.response?.status === 404) break; // definitively gone
            if (attempt < 2) await sleep(800 * (attempt + 1));
          }
        }
        if (resumed) {
          // 2026-09-29 (item 6.3) — a lost completion response must
          // recover the already-created batch instead of starting a
          // fresh session for work the server already finished.
          if (resumed.status === "completed" && resumed.batch_id) {
            localStorage.removeItem(key);
            const { data: batch } = await api.get(`/upload_batches/${resumed.batch_id}`);
            return { ...batch.batch, already_completed: true };
          }
          if (resumed.status === "completing" || resumed.status === "uploading") {
            sessionId = parsed.sessionId;
            chunkSize = resumed.chunk_size;
            totalChunks = resumed.total_chunks;
            received = new Set(resumed.chunks_received || []);
          }
          // status === "failed"/"expired": fall through to a fresh session below.
        }
      }
    } catch { /* corrupt cache entry — fall through to a fresh session */ }
  }

  if (!sessionId) {
    const { data: created } = await api.post(`/projects/${projectId}/upload_sessions`, {
      filename: file.name, total_size: file.size, sha256: hash, mode,
    });
    sessionId = created.session.id;
    chunkSize = created.chunk_size;
    totalChunks = created.total_chunks;
    localStorage.setItem(key, JSON.stringify({ sessionId, hash }));
  }

  onStage("uploading");
  const pending = [];
  for (let i = 0; i < totalChunks; i++) {
    if (!received.has(i)) pending.push(i);
  }
  let completedCount = totalChunks - pending.length;
  onProgress(completedCount / totalChunks);

  // 2026-09-29 (item 6.5) — a single dropped PUT on a flaky mobile
  // connection used to abort the ENTIRE upload (Promise.all rejects on
  // the first failure), even though the session itself stayed
  // perfectly resumable server-side. Retry each chunk a bounded number
  // of times with backoff before giving up — the common transient-
  // network case now recovers without the user having to reselect the
  // file.
  let cursor = 0;
  async function worker() {
    while (cursor < pending.length) {
      const idx = pending[cursor++];
      const start = idx * chunkSize;
      const blob = file.slice(start, Math.min(start + chunkSize, file.size));
      let lastErr = null;
      for (let attempt = 0; attempt < CHUNK_PUT_MAX_ATTEMPTS; attempt++) {
        try {
          await api.put(`/upload_sessions/${sessionId}/chunks/${idx}`, blob, {
            headers: { "Content-Type": "application/octet-stream" },
            timeout: 120000,
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (attempt < CHUNK_PUT_MAX_ATTEMPTS - 1) await sleep(CHUNK_PUT_RETRY_DELAYS_MS[attempt]);
        }
      }
      if (lastErr) throw lastErr;
      completedCount += 1;
      onProgress(completedCount / totalChunks);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CHUNK_UPLOAD_CONCURRENCY, pending.length || 1) }, worker));

  onStage("verifying");
  const { data: batch } = await api.post(`/upload_sessions/${sessionId}/complete`, null, { timeout: 60000 });
  localStorage.removeItem(key);
  return batch;
}

export default function UploadSheetsModal({ projectId, projectName, mode = "new", onClose, onUploaded }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("file"); // "file" | "link"
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [stage, setStage] = useState(null); // "hashing" | "uploading" | "verifying"
  const [error, setError] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [sheetSetName, setSheetSetName] = useState("");
  const [linkDiscipline, setLinkDiscipline] = useState("general");
  const [linkRevision, setLinkRevision] = useState("0");
  const [linkNotes, setLinkNotes] = useState("");
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isRevision = mode === "revision";
  const titleLabel = isRevision ? "Upload revision package" : "Add sheets";
  const ctaLabel = isRevision ? "Process revision package" : "Process plan set";

  const [resumeHint, setResumeHint] = useState(false);
  useEffect(() => {
    if (!file || !user?.id) { setResumeHint(false); return; }
    setResumeHint(!!localStorage.getItem(sessionStorageKey(user.id, projectId, mode, file.name, file.size)));
  }, [file, projectId, mode, user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (tab === "file") {
      if (!file) return;
      setSubmitting(true); setError(null); setUploadPct(0); setStage("hashing");
      try {
        const data = await uploadResumable(user?.id, projectId, mode, file, setUploadPct, setStage);
        if (onUploaded) onUploaded(data);
        onClose();
        navigate(`/project/${projectId}/ingest/${data.id}`);
      } catch (err) {
        setError(err?.response?.data?.detail || "Upload failed. Reselecting the same file will resume where it left off.");
      } finally {
        setSubmitting(false); setUploadPct(null); setStage(null);
      }
    } else {
      // Iter 92.1 — import-by-link
      const url = linkUrl.trim();
      if (!url) { setError("Paste a link first."); return; }
      setSubmitting(true); setError(null);
      try {
        const { data } = await api.post(
          `/projects/${projectId}/sheets/import-url`,
          {
            url,
            sheet_set_name: sheetSetName.trim() || undefined,
            discipline: linkDiscipline || "general",
            revision: linkRevision || "0",
            notes: linkNotes.trim() || undefined,
          },
          { timeout: 320000 }, // 5m + small buffer
        );
        if (onUploaded) onUploaded(data);
        onClose();
      } catch (err) {
        const d = err?.response?.data?.detail;
        setError(
          typeof d === "string" ? d :
          (d && d.error) ? d.error :
          "ForgedOps could not import this link."
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      data-testid="upload-sheets-modal"
      data-mode={mode}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg border border-[var(--fop-border-2)] bg-[var(--fop-surface)] shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--fop-border)]">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-steel)]">
              Plan Set {isRevision ? "Revision" : "Ingestion"}
            </div>
            <h2 className="font-display text-xl text-[var(--fop-text)] mt-0.5 truncate" title={projectName}>
              {titleLabel}
            </h2>
            {projectName && (
              <div className="text-[11px] text-[var(--fop-text-3)] truncate">{projectName}</div>
            )}
          </div>
          {/* 2026-09-29 (item 6, "closing the modal must have explicit,
              predictable behavior") — disabled instead of silently
              allowed while a chunk transfer is actually in flight, so a
              user can never lose visibility into an upload that IS
              still progressing server-side by accidentally dismissing
              the modal. The upload session itself remains resumable
              either way; this only controls whether this modal
              instance keeps watching it to completion. */}
          <button
            type="button"
            onClick={submitting ? undefined : onClose}
            disabled={submitting}
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Close"
            title={submitting ? "Upload in progress — please wait for it to finish or fail before closing" : "Close"}
            data-testid="upload-sheets-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Iter 92.1 — Tab switcher */}
          <div className="flex items-center gap-1 border-b border-[var(--fop-border)] -mt-1">
            <TabBtn active={tab === "file"} onClick={() => { setTab("file"); setError(null); }} testid="upload-tab-file" icon={Upload}>Upload from Computer</TabBtn>
            <TabBtn active={tab === "link"} onClick={() => { setTab("link"); setError(null); }} testid="upload-tab-link" icon={LinkIcon}>Import from Link</TabBtn>
          </div>

          {tab === "file" ? (
          <>
          <label
            className="block border-2 border-dashed rounded-md px-4 py-6 text-center cursor-pointer hover:border-[var(--fop-primary)] transition-colors"
            style={{ borderColor: file ? "var(--fop-primary)" : "var(--fop-border-2)" }}
            data-testid="upload-sheets-filebox"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="upload-sheets-file"
            />
            <Upload size={22} className="mx-auto mb-2 text-[var(--fop-text-3)]" />
            <div className="font-mono text-xs text-[var(--fop-text)]">
              {file ? file.name : "Drop or click to choose a PDF"}
            </div>
            <div className="text-[10px] text-[var(--fop-text-3)] mt-1">
              {file
                ? `${(file.size / 1024 / 1024).toFixed(1)} MB · ${isRevision ? "revision package" : "single OR multi-sheet"}`
                : "Single-sheet OR full plan set — up to 80 MB"}
            </div>
          </label>

          {resumeHint && !submitting && (
            <div
              className="text-[10px] font-mono text-amber-400 border border-amber-400/30 rounded-sm px-2 py-1"
              data-testid="upload-resume-hint"
            >
              A previous upload of this exact file was interrupted — submitting will resume it instead of starting over.
            </div>
          )}
          {submitting && uploadPct !== null && (
            <div data-testid="upload-progress">
              <div className="h-1.5 rounded-full bg-[var(--fop-border)] overflow-hidden">
                <div
                  className="h-full bg-[var(--fop-primary)] transition-all"
                  style={{ width: `${Math.round((stage === "uploading" ? uploadPct : stage === "verifying" ? 1 : 0) * 100)}%` }}
                  data-testid="upload-progress-bar"
                />
              </div>
              <div className="text-[10px] font-mono text-[var(--fop-text-3)] mt-1" data-testid="upload-progress-stage">
                {stage === "hashing" && "Preparing… checksumming file"}
                {stage === "uploading" && `Uploading… ${Math.round(uploadPct * 100)}%`}
                {stage === "verifying" && "Verifying upload integrity…"}
              </div>
            </div>
          )}

          <div className="text-[11px] text-[var(--fop-text-3)] leading-relaxed border-l-2 border-[var(--fop-steel)] pl-3 py-1">
            <div className="flex items-start gap-2 mb-1">
              <FileText size={11} className="mt-0.5 shrink-0" />
              <span>Each page is split into a candidate sheet with extracted metadata + confidence.</span>
            </div>
            <div className="flex items-start gap-2">
              <Layers size={11} className="mt-0.5 shrink-0" />
              <span>
                {isRevision
                  ? "Revision candidates are matched against existing live sheets; possible duplicates are flagged."
                  : "Confirm metadata in the review screen — nothing publishes until you approve."}
              </span>
            </div>
          </div>
          </>
          ) : (
          <div className="space-y-3" data-testid="upload-link-form">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Link URL</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://www.dropbox.com/s/… or Google Drive / OneDrive / Box / direct PDF"
                className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border-2)] rounded-md px-3 py-2 text-sm text-[var(--fop-text)] focus:outline-none focus:border-[var(--fop-primary)]"
                data-testid="upload-link-url"
                required
              />
              <div className="text-[10px] text-[var(--fop-text-3)] mt-1">ForgedOps downloads the file server-side, validates it, and stores it in your tenant. The link is never used as the permanent source.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Sheet set name (optional)</label>
                <input value={sheetSetName} onChange={(e) => setSheetSetName(e.target.value)} placeholder="Working Set"
                  className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border-2)] rounded-md px-3 py-2 text-sm text-[var(--fop-text)]"
                  data-testid="upload-link-name" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Discipline</label>
                <select value={linkDiscipline} onChange={(e) => setLinkDiscipline(e.target.value)}
                  className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border-2)] rounded-md px-3 py-2 text-sm text-[var(--fop-text)]"
                  data-testid="upload-link-discipline">
                  <option value="general">General</option>
                  <option value="architectural">Architectural</option>
                  <option value="structural">Structural</option>
                  <option value="civil">Civil</option>
                  <option value="mep">MEP</option>
                  <option value="electrical">Electrical</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Revision label</label>
                <input value={linkRevision} onChange={(e) => setLinkRevision(e.target.value)} placeholder="0"
                  className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border-2)] rounded-md px-3 py-2 text-sm text-[var(--fop-text)]"
                  data-testid="upload-link-revision" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Notes (optional)</label>
                <input value={linkNotes} onChange={(e) => setLinkNotes(e.target.value)} placeholder="Source agency, plan room id…"
                  className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border-2)] rounded-md px-3 py-2 text-sm text-[var(--fop-text)]"
                  data-testid="upload-link-notes" />
              </div>
            </div>
          </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-xs text-red-400 font-mono" data-testid="upload-sheets-error">
              <AlertCircle size={12} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[var(--fop-border)] flex items-center justify-end gap-2 bg-[var(--fop-surface-2)] rounded-b-lg">
          <button type="button" onClick={onClose} className="btn btn-ghost text-xs">Cancel</button>
          <button
            type="submit"
            disabled={submitting || (tab === "file" ? !file : !linkUrl.trim())}
            className="btn btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="upload-sheets-submit"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {tab === "link" ? "Import Plans" : (
              submitting
                ? (stage === "hashing" ? "Preparing…"
                  : stage === "verifying" ? "Verifying…"
                  : uploadPct !== null ? `Uploading ${Math.round(uploadPct * 100)}%` : "Working…")
                : ctaLabel
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-[0.16em] border-b-2 -mb-px transition-colors ${
        active
          ? "border-[var(--fop-primary)] text-[var(--fop-text)]"
          : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
      }`}
    >
      <Icon size={12} />
      {children}
    </button>
  );
}
